import BaseOption from "%/dynamic-component-editor/classes/base-option";
import { inspectorTools } from "%/dynamic-component-editor/inspector/inspector-tool-map";
import {
  ATTENTION_ANIMATION_PRESETS,
  ENTRANCE_ANIMATION_PRESETS,
  EVENT_PURPOSE_ATTENTION,
  EVENT_PURPOSE_DISMISS,
  EVENT_PURPOSE_REVEAL,
  EXIT_ANIMATION_PRESETS,
  HOVER_ANIMATION_PRESETS,
  TRIGGERED_EVENT_KEY,
  TRIGGER_EVENT_ID,
} from "@bloglovin/vue-component-library/animation";

export default class AnimationEffects extends BaseOption {
  static animationTriggers = [
    {
      id: "onAppear",
      name: "On Appear",
    },
    {
      id: "onClick",
      name: "On Click",
    },
    {
      id: "onHover",
      name: "On Hover",
    },
    {
      id: "onEvent",
      name: "On Event",
    },
    {
      id: "mouseenter",
      name: "Mouse Enter",
    },
    {
      id: "mouseleave",
      name: "Mouse Leave",
    },
    {
      id: "clickoutside",
      name: "Click Outside",
    },
    {
      id: "onDoubleClick",
      name: "On Double Click / Double Tap",
    },
    {
      id: "onLongPress",
      name: "On Right Click / Long Press",
    },
    {
      id: "onSwipeLeft",
      name: "On Swipe Left",
    },
    {
      id: "onSwipeRight",
      name: "On Swipe Right",
    },
    {
      id: "onSwipeUp",
      name: "On Swipe Up",
    },
    {
      id: "onSwipeDown",
      name: "On Swipe Down",
    },
    {
      id: "onPinch",
      name: "On Pinch",
    },
    {
      id: "onSpread",
      name: "On Spread",
    },
    {
      id: "onRotateLeft",
      name: "On Rotate Left",
    },
    {
      id: "onRotateRight",
      name: "On Rotate Right",
    },
  ];

  static triggerEventOption = {
    [TRIGGER_EVENT_ID]: {
      custom: true,
    },
  };

  // The preset catalogs live in the VCL animation engine — creator only
  // configures; VCL runs.
  static defaultAnimationOptions = ENTRANCE_ANIMATION_PRESETS;

  static hoverAnimationOptions = HOVER_ANIMATION_PRESETS;

  static attentionAnimationOptions = ATTENTION_ANIMATION_PRESETS;

  static exitAnimationOptions = EXIT_ANIMATION_PRESETS;

  // The onAppear Scroll section's mode choices.
  static scrollModes = [
    { id: "once", name: "Play once" },
    { id: "scrub", name: "Scrub with scroll" },
  ];

  // What an onEvent listener DOES when its event fires — drives both the
  // preset catalog it picks from and the runtime state machine.
  static eventPurposes = [
    { id: EVENT_PURPOSE_ATTENTION, name: "Attention (plays in place)" },
    { id: EVENT_PURPOSE_REVEAL, name: "Reveal (hidden until triggered, toggles)" },
    { id: EVENT_PURPOSE_DISMISS, name: "Dismiss (hides the element)" },
  ];

  // What an onEvent entry rebuilds onto when its purpose switches.
  static defaultPresetByPurpose = {
    [EVENT_PURPOSE_ATTENTION]: "pulse",
    [EVENT_PURPOSE_REVEAL]: "slide-down",
    [EVENT_PURPOSE_DISMISS]: "fade-out",
  };

  // Curated one-click defaults: what the + button drops in per trigger.
  static defaultPresetByTrigger = {
    onAppear: "fade-in",
    onHover: "soft-scale",
    onClick: "soft-scale",
    onEvent: "pulse",
    mouseenter: "fade-in",
    mouseleave: "fade-in",
    clickoutside: "fade-in",
  };

  static eventTriggerSettings = [
    { id: TRIGGERED_EVENT_KEY, label: "Triggered Event" },
  ];

  // onEvent listeners may opt to ignore events they emitted themselves.
  static eventListenerSettings = [
    { id: "excludeSelf", label: "Ignore events I emitted", type: "checkbox", default: false },
  ];

  static hoverAnimationSettings = [
    { id: "duration", label: "Duration", unit: "ms" },
    { id: "delay", label: "Delay", unit: "ms" },
    { id: "timingFunction", label: "Timing Function", unit: "" },
    { id: "effect", label: "", unit: "" },
  ];

  // removeWhenInvisible is gone from the UI — the onEvent PURPOSE control
  // owns show/hide semantics now (legacy stored flags still read as reveal).
  static defaultAnimationSettings = [
    { id: "duration", label: "Duration", unit: "ms" },
    { id: "delay", label: "Delay", unit: "ms" },
    { id: "timingFunction", label: "Timing Function", unit: "" },
    { id: "iteration", label: "Iteration", unit: "" },
    { id: "autoReset", label: "Auto Reset", type: "checkbox", default: false },
    { id: "initialState", label: "", unit: "" },
  ];

  static initialStateProperties = [
    { id: "opacity", label: "Opacity", unit: "", type: "number", step: "any", min: 0, max: 1 },
    { id: "scale", label: "Scale", unit: "", type: "number", step: "any", min: 0 },
    { id: "rotate", label: "Rotate", unit: "deg", type: "number" },
    { id: "offsetX", label: "Offset X", unit: "px", min: -5000, max: 5000, type: "number" },
    { id: "offsetY", label: "Offset Y", unit: "px", min: -5000, max: 5000, type: "number" },
    { id: "blur", label: "Blur", unit: "px", type: "number" },
  ];

  static userInterfaceComponent = () => {
    return {
      component: inspectorTools.ANIMATIONS_EFFECTS_TOOL_SET,
      ignoreDefaultIfNotSetForState: [BaseOption.$states.HOVER],
      props: {
        title: "Effects",
      },
    };
  };

  static defaultValue = {};
}
